import { mixed, number, object, string } from "yup";

import {
  HomeFixtures,
  HomeType,
  OccupationType,
  RoofShape,
  SkirtingType,
  UnitLocation,
} from "@covertree/new-business-graphql";

import { FormSelectable, OptionType } from "@covertree/types";
import { homeTypes } from "@covertree/ui";
import { StateCode } from "@covertree/consts";

export type BasicHomeDetailsForm = {
  homeType?: HomeType;
  manufacturer?: OptionType<string>;
  makeCustom?: string;
  modelHome?: string;
  modelYear?: OptionType<number>;
  totalSquareFootage?: number | null;
  homeLength?: number | null;
  homeWidth?: number | null;
};

export type LocationAndRoofDetailsForm = {
  communityName?: OptionType<string>;
  location?: UnitLocation;
  roofShape?: RoofShape;
  roofYearYyyy?: number;
  homeType?: HomeType;
  skirtingType?: SkirtingType;
  homeHudNumber?: string | null;
};

export type HomeDetailsForm = {
  homeFixtures?: HomeFixtures;
} & BasicHomeDetailsForm &
  LocationAndRoofDetailsForm;

export const houseDetailsDict = [
  "House shape",
  "House roof",
  "House exterior",
  "Windows & doors",
  "Ceilings and walls",
  "Interior flooring",
  "Interior amenities",
] as const;

export interface BuildingTypeData {
  name: string;
  description?: string;
}

export type HouseTypeDetails = Record<
  (typeof houseDetailsDict)[number],
  BuildingTypeData
>;

export type FixturesAndFinishesType = FormSelectable<HomeFixtures> &
  HouseTypeDetails & { value: HomeFixtures };

export const fixturesAndFinishesTypes: FixturesAndFinishesType[] = [
  {
    title: "Standard",
    value: HomeFixtures.Standard,
    image: "/images/fixtures/standard-fixtures.png",
    imageAlt: "standard",
    subtitle:
      "Rectangular in shape, with a flatter roof. The exterior of the home is typically covered in a single color vinyl or aluminum siding. The interior of the home is focused on the basics including paneled walls, standard appliances, laminated cabinets, and plastic showers/tubs.",
    "House shape": { name: "Rectangular in shape" },
    "House roof": {
      name: "Lower Pitched/Sloping (Flatter) Roofs often with composite shingles or rolled roofing with sealant (e.g. Kool Seal, Henry)",
    },
    "House exterior": {
      name: "Vinyl and aluminum siding, often with a singular color",
    },
    "Windows & doors": { name: "Standard windows and doors" },
    "Ceilings and walls": { name: "Paneling throughout. Lower ceilings." },
    "Interior flooring": {
      name: "Particle board under flooring, low weight carpeting and padding. Carpeting may be ran under the wall.",
    },
    "Interior amenities": {
      name: "Focused on the basics: such as standard appliances, laminated cabinets, plastic showers.",
    },
  },
  {
    title: "A few extras",
    value: HomeFixtures.AFewExtras,
    image: "/images/fixtures/extra-fixtures.png",
    imageAlt: "a few extras",
    subtitle:
      "Rectangular in shape, with a higher sloping roof; some exterior accents added, with vinyl siding; inside has higher ceilings and some upgrades.",
    "House shape": { name: "Rectangular in shape" },
    "House roof": {
      name: "Steeper Pitched/Sloping Roofs with 3-tab composite shingles or standing seam metal. Roof may also have an additional accent ridge over the entry of the home.",
    },
    "House exterior": {
      name: "Vinyl siding, may feature additional accent color(s)",
    },
    "Windows & doors": {
      name: "Higher efficiency windows and doors, may be energy star certified.",
    },
    "Ceilings and walls": {
      name: "May combine paneling with drywall. 8 feet or higher ceilings.",
    },
    "Interior flooring": {
      name: "OSB or plywood under flooring. Higher weight carpeting with thicker padding. With tile or laminate flooring.",
    },
    "Interior amenities": {
      name: "Some interior upgrades such as kitchen islands, upgraded appliances, tile backsplash, solid counter tops, and fiberglass showers.",
    },
  },
  {
    title: "Top of the line",
    value: HomeFixtures.TopOfTheLine,
    image: "/images/fixtures/top-fixtures.png",
    imageAlt: "top of the line",
    subtitle:
      "May not be rectangular in shape and may incorporate a covered porch; outside may use high-grade siding; the interior is often drywall throughout and may include upgrades.",
    "House shape": {
      name: "Rectangular in shape but may also incorporate covered porches causing the interior of the home to be L-Shaped.",
    },
    "House roof": {
      name: "Steeper Pitched/Sloping Roofs with 3-tab composite shingles or standing seam metal. Roof may also have an additional accent ridge over the entry of the home.",
    },
    "House exterior": {
      name: "Vinyl, Hardi-Panel, or SmartPanel siding, may feature additional accent color(s).",
    },
    "Windows & doors": {
      name: "Higher efficiency windows and doors, may be energy star certified.",
    },
    "Ceilings and walls": {
      name: "Often drywall throughout. 8 feet or higher ceilings with exposed beams.",
    },
    "Interior flooring": {
      name: "OSB or plywood under flooring. Higher weight carpeting with thicker padding. With tile or laminate flooring.",
    },
    "Interior amenities": {
      name: "Many interior upgrades such as kitchen islands, stainless steel appliances, tile backsplash, stone counter tops, or fiberglass showers.",
    },
  },
];

export const roofShapes = [
  { label: "Gable", value: RoofShape.Gable },
  { label: "Hip", value: RoofShape.Hip },
  { label: "Flat", value: RoofShape.Flat },
  { label: "Bowed/Curved/Arched", value: RoofShape.BowedCurvedArched },
  { label: "Shed", value: RoofShape.Shed },
  { label: "Not Listed", value: RoofShape.NotListed },
] as const;

export const roofShapesValues = roofShapes.map(({ value }) => value);

export const skirtingOptions = [
  { label: "Blank", value: null },
  { label: "Not Fully Enclosed", value: SkirtingType.NotFullyEnclosed },
  { label: "Vinyl", value: SkirtingType.Vinyl },
  { label: "Lattice", value: SkirtingType.Lattice },
  { label: "Metal", value: SkirtingType.Metal },
  { label: "Masonry/Brick", value: SkirtingType.MasonryBrick },
  { label: "Not Listed", value: SkirtingType.NotListed },
] as const;

export const occupationOptions = [
  { label: "Blank", value: null },
  { label: "Accountant", value: OccupationType.Accountant },
  { label: "Artist / Designer", value: OccupationType.ArtistDesigner },
  { label: "Business Owner", value: OccupationType.BusinessOwner },
  { label: "Construction Worker", value: OccupationType.ConstructionWorker },
  { label: "Customer Service", value: OccupationType.CustomerService },
  { label: "Doctor / Nurse", value: OccupationType.DoctorNurse },
  { label: "Driver", value: OccupationType.Driver },
  { label: "Educator / Teacher", value: OccupationType.EducatorTeacher },
  { label: "Engineer", value: OccupationType.Engineer },
  { label: "Executive / Manager", value: OccupationType.ExecutiveManager },
  { label: "Financial Analyst", value: OccupationType.FinancialAnalyst },
  { label: "Healthcare Worker", value: OccupationType.HealthcareWorker },
  {
    label: "IT / Software Professional",
    value: OccupationType.ItSoftwareProfessional,
  },
  { label: "Lawyer", value: OccupationType.Lawyer },
  { label: "Marketing / Sales", value: OccupationType.MarketingSales },
  { label: "Mechanic / Technician", value: OccupationType.MechanicTechnician },
  { label: "Military", value: OccupationType.Military },
  { label: "Police / Firefighter", value: OccupationType.PoliceFirefighter },
  { label: "Real Estate Agent", value: OccupationType.RealEstateAgent },
  { label: "Retail Worker", value: OccupationType.RetailWorker },
  {
    label: "Scientist / Researcher",
    value: OccupationType.ScientistResearcher,
  },
  { label: "Student", value: OccupationType.Student },
  { label: "Unemployed", value: OccupationType.Unemployed },
  { label: "Writer / Editor", value: OccupationType.WriterEditor },
] as const;

export const getLocationTypes = (
  isMyOwnLand: boolean
): OptionType<UnitLocation>[] => {
  return [
    {
      value: UnitLocation.LargeManufacturedHome,
      label:
        "In a large manufactured home community (more than or equal to 25 units)",
    },
    {
      value: UnitLocation.SmallManufacturedHome,
      label: "In a small manufactured home community (less than 25 units)",
    },
    {
      value: UnitLocation.OnMyOwnLand,
      label: isMyOwnLand ? "On my own land" : "This home is on private land",
    },
  ];
};

const currentYear = new Date().getFullYear();

const homeTypeValidator = (state?: StateCode) =>
  mixed<HomeType>()
    .transform((v) => (!v ? undefined : v))
    .required("Home Type is required")
    .test("isValidHomeType", "Select one of provided types", (v) => {
      return v && Object.keys(homeTypes).includes(v);
    })
    .test(
      "ky-modular-restriction",
      "Modular homes are currently ineligible in Kentucky.",
      (v) => !(state === StateCode.KY && v === HomeType.Modular)
    )
    .test(
      "nc-modular-restriction",
      "Modular homes are currently ineligible in North Carolina.",
      (v) => !(state === StateCode.NC && v === HomeType.Modular)
    )
    .test(
      "al-duplex-restriction",
      "Duplex homes are currently ineligible in Alabama.",
      (v) => !(state === StateCode.AL && v === HomeType.Duplex)
    )
    .test(
      "al-crossmod-restriction",
      "CrossMod homes are currently ineligible in Alabama.",
      (v) => !(state === StateCode.AL && v === HomeType.CrossMod)
    );

const manufacturerValidator = mixed()
  .transform((v) => (!v ? undefined : v))
  .required("Manufacturer name is required");

const makeCustomValidator = string()
  .nullable()
  .transform((value) => value || undefined)
  .max(100, "Custom make is too long")
  .test(
    "no-leading-trailing-spaces",
    "Custom make cannot have leading or trailing spaces",
    (value) => {
      if (!value) return true;
      return value === value.trim();
    }
  )
  .optional();

const modelHomeValidator = string()
  .nullable()
  .transform((value) => value || undefined)
  .max(100, "Model name is too long")
  .test(
    "no-leading-trailing-spaces",
    "Model name cannot have leading or trailing spaces",
    (value) => {
      if (!value) return true;
      return value === value.trim();
    }
  )
  .optional();

const modelYearValidator = mixed()
  .transform((v) => (!v ? undefined : v))
  .required("Model Year is required")
  .test(
    "is-valid-selection",
    "Please select a valid year from the list",
    (value) => {
      if (!value) return true;
      return !(typeof value === "object" && "invalid" in value);
    }
  );

const totalSquareFootageValidator = number()
  .transform((v, o) => (o === "" ? null : v))
  .required("Square footage is required")
  .typeError("Square footage must be a number")
  .positive("Please type in a positive number")
  .integer("Please type in a whole number")
  .min(100, "Square footage cannot be less than 100")
  .max(9999, "Square footage cannot be greater than 9,999");

const homeWidthValidator = number()
  .transform((v, o) => (o === "" ? null : v))
  .optional()
  .nullable()
  .typeError("Width must be a number")
  .positive("Width must be greater than 0")
  .integer("Please type in a whole number")
  .max(500, "Width cannot be greater than 500")
  .test("width-required", "Width is required", function (value) {
    if (value === undefined) return true;

    const { homeLength, totalSquareFootage } = this.parent;

    if ((homeLength && homeLength > 0) || !totalSquareFootage) {
      return !!value && Number(value) > 0;
    }
    return true;
  });

const homeLengthValidator = number()
  .transform((v, o) => (o === "" ? null : v))
  .optional()
  .nullable()
  .typeError("Length must be a number")
  .positive("Length must be greater than 0")
  .integer("Please type in a whole number")
  .max(500, "Length cannot be greater than 500")
  .test("length-required", "Length is required", function (value) {
    if (value === undefined) return true;

    const { homeWidth, totalSquareFootage } = this.parent;

    if ((homeWidth && homeWidth > 0) || !totalSquareFootage) {
      return !!value && Number(value) > 0;
    }
    return true;
  });

const communityNameValidator = mixed<OptionType<string>>()
  .transform((v) => (!v ? undefined : v.value))
  .typeError("Community name is required")
  .when("location", {
    is: (homeLocation: UnitLocation) =>
      homeLocation === UnitLocation.LargeManufacturedHome ||
      homeLocation === UnitLocation.SmallManufacturedHome,
    then: () =>
      object({
        value: string().required(),
        label: string().required(),
      })
        .test("exist", "Community name is required", ({ value }) => !!value)
        .required("Community name is required")
        .typeError("Community name is required"),
  });

const locationValidator = mixed<UnitLocation>()
  .oneOf(
    Object.keys(UnitLocation) as UnitLocation[],
    "Choose one of the provided location types"
  )
  .required("Choose one of the provided location types");

const roofShapeValidator = mixed<RoofShape>()
  .required("Roof Shape is required")
  .oneOf(roofShapesValues, "Choose valid value");

const roofYearYyyyValidator = (modelYear?: number) =>
  number()
    .required("This field is required")
    .typeError("Roof year must be a number")
    // eslint-disable-next-line func-names
    .test("min", "Cannot be before manufactured year", function () {
      const manufactureYear = modelYear ?? this.parent?.modelYear;
      return manufactureYear > currentYear ? currentYear : manufactureYear;
    })
    .max(currentYear, "Cannot be in the future");

const homeHudNumberValidator = string()
  .nullable()
  .transform((value) => (value === "" ? undefined : value?.toUpperCase()))
  .max(25, "HUD code must be at most 25 characters")
  .matches(
    /^[A-Z0-9]*$/,
    "HUD code must contain only uppercase letters and numbers"
  )
  .optional();

const skirtingTypeValidator = string()
  .nullable()
  .transform((value) => (value ? value : undefined))
  .oneOf(
    [...skirtingOptions.map(({ value }) => value), undefined],
    "Please select one of the options"
  );

const homeFixturesValidator = mixed<HomeFixtures>()
  .oneOf(
    fixturesAndFinishesTypes.map(({ value }) => value),
    "Please select one of the options"
  )
  .required("Please select one of the options");

const tenantOnlyQuestionsValidators = (state?: StateCode) => ({
  communityName: communityNameValidator,
  homeType: homeTypeValidator(state),
  location: locationValidator,
});

const homeDetailsValidators = (state?: StateCode) => ({
  manufacturer: manufacturerValidator,
  makeCustom: makeCustomValidator,
  modelHome: modelHomeValidator,
  homeType: homeTypeValidator(state),
  modelYear: modelYearValidator,
  homeLength: homeLengthValidator,
  homeWidth: homeWidthValidator,
  totalSquareFootage: totalSquareFootageValidator,
});

const tenantHomeDetailsValidators = (state?: StateCode) => ({
  homeType: homeTypeValidator(state),
  location: locationValidator,
  communityName: communityNameValidator,
});

const locationAndRoofDetailsValidators = (
  isTenant: boolean,
  state?: StateCode,
  modelYear?: number
) => ({
  ...(isTenant
    ? { ...tenantOnlyQuestionsValidators(state) }
    : {
        location: locationValidator,
        communityName: communityNameValidator,
        roofShape: roofShapeValidator,
        roofYearYyyy: roofYearYyyyValidator(modelYear),
        homeHudNumber: homeHudNumberValidator,
        skirtingType: skirtingTypeValidator,
      }),
});

const nonTenantValidations = (state?: StateCode) => ({
  ...homeDetailsValidators(state),
  ...tenantOnlyQuestionsValidators(state),
  roofShape: roofShapeValidator,
  roofYearYyyy: roofYearYyyyValidator(),
  homeHudNumber: homeHudNumberValidator,
  skirtingType: skirtingTypeValidator,
  homeFixtures: homeFixturesValidator,
});

export const homeFixturesFormSchema = object({
  homeFixtures: homeFixturesValidator,
});

export const getLocationAndRoofDetailsValidators = (
  isTenant: boolean,
  state?: StateCode,
  modelYear?: number
) => object(locationAndRoofDetailsValidators(isTenant, state, modelYear));

export const getD2CHouseDetailsFormSchema = (
  isTenant: boolean,
  state?: StateCode
) =>
  object(
    isTenant ? tenantHomeDetailsValidators(state) : homeDetailsValidators(state)
  );

export const getAgentHouseDetailsFormSchema = (
  isTenant: boolean,
  state?: StateCode
) =>
  object(
    isTenant
      ? tenantOnlyQuestionsValidators(state)
      : nonTenantValidations(state)
  );
